import React, { useState, useMemo, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import GradientIcon from '../components/GradientIcon';
import SpeakersWidget from '../components/SpeakersWidget';
import SpeakerSpotlight from '../components/SpeakerSpotlight';
import BookShowcase from '../components/BookShowcase';
import CTASection from '../components/CTASection';
import WhySection from '../components/WhySection';
import { SPONSOR_FORM_URL } from '../config';
// import GroupPackages from '../components/GroupPackages'; // Pachete de grup - entire section commented out
import ExternalLink from '../components/ExternalLink';
import SpeakerSocialLinks from '../components/SpeakerSocialLinks';
import CountdownTimer from '../components/CountdownTimer';
import { useAvailableSeats } from '../hooks/useAvailableSeats';

const PRICING_TIERS = [
  {
    period: 1,
    startDate: '2025-11-19',
    endDate: '2025-11-30',
    standard: 380,
    vip: 580,
    ultraVip: 1500,
    standardSeats: 3100,
    vipSeats: 750,
    ultraVipSeats: 150,
  },
  {
    period: 2,
    startDate: '2025-12-01',
    endDate: '2025-12-31',
    standard: 420,
    vip: 750,
    ultraVip: 1700,
    standardSeats: 3100,
    vipSeats: 750,
    ultraVipSeats: 150,
  },
  {
    period: 3,
    startDate: '2026-01-01',
    endDate: '2026-01-31',
    standard: 450,
    vip: 900,
    ultraVip: 2000,
    standardSeats: 3100,
    vipSeats: 750,
    ultraVipSeats: 150,
  },
  {
    period: 4,
    startDate: '2026-02-01',
    endDate: '2026-02-28',
    standard: 470,
    vip: 1080,
    ultraVip: 2500,
    standardSeats: 3100,
    vipSeats: 750,
    ultraVipSeats: 150,
  },
  {
    period: 5,
    startDate: '2026-02-28',
    endDate: '2026-05-31',
    standard: 500,
    vip: 1260,
    ultraVip: 3000,
    standardSeats: 3100,
    vipSeats: 750,
    ultraVipSeats: 150,
  },
  {
    period: 6,
    startDate: '2026-06-01',
    endDate: '2026-06-10',
    standard: 560,
    vip: 1440,
    ultraVip: 3500,
    standardSeats: 3100,
    vipSeats: 750,
    ultraVipSeats: 150,
  },
  {
    period: 7,
    startDate: '2026-06-11',
    endDate: '2026-06-19',
    standard: 630,
    vip: 1620,
    ultraVip: 4000,
    standardSeats: 3100,
    vipSeats: 750,
    ultraVipSeats: 150,
  },
];

const ALL_PACKAGES = [
  {
    id: 'standard-economy',
    title: 'Standard Economy',
    price: 248,
    description: 'Pachet economic pentru participare',
    featured: false,
    benefits: [
      'Brain Mapping (în valoare de 500€)',
      'Locuri în zonele: J, K, L și M',
      'Acces complet la eveniment (11:00 - 19:00)'
    ]
  },
  {
    id: 'standard-discount',
    title: 'Standard Discount',
    price: 410,
    description: 'Pachet cu discount special',
    featured: false,
    benefits: [
      'Brain Mapping (în valoare de 500€)',
      'Locuri în zonele: J, K, L, M și loje',
      'Acces complet la eveniment (11:00 - 19:00)',
      'Minge Antistres'
    ]
  },
  {
    id: 'standard',
    title: 'Standard',
    price: 395,
    description: 'Pachet de bază pentru participare',
    featured: false,
    benefits: [
      'Brain Mapping (în valoare de 500€)',
      'Locuri în zonele: F, G și loje',
      'Acces complet la eveniment (11:00 - 19:00)',
      'Minge Antistres'
    ]
  },
  {
    id: 'standard-premium',
    title: 'Standard Premium',
    price: 449,
    description: 'Pachet standard îmbunătățit',
    featured: false,
    benefits: [
      'Brain Mapping (în valoare de 500€)',
      'Locuri premium în zonele: H și I',
      'Acces complet la eveniment (11:00 - 19:00)',
      'Minge Antistres'
    ]
  },
  {
    id: 'vip',
    title: 'VIP',
    price: 649,
    description: 'Experiență premium și beneficii speciale',
    featured: true,
    benefits: [
      'Brain Mapping cu Diana (în valoare de 1000€)',
      'Locuri în fața scenei, în zonele: A, B, C, D și E',
      'Acces complet la eveniment (11:00 - 19:00)',
      'Acces în comunitatea VIP',
      'Cupon pentru prânz',
      'Brain Notes – workbook fizic',
      'Minge Antistres',
      'Goodie Bag (în valoare de 100€)'
    ]
  },
  {
    id: 'ultra-vip',
    title: 'Ultra VIP',
    price: 2500,
    description: 'Experiență exclusivă',
    featured: false,
    benefits: [
      'Brain Mapping cu Diana (în valoare de 1000€)',
      'Locuri în primele 3 rânduri în zonele: A, D și E',
      'Acces prioritar în sală',
      'Acces complet la eveniment (11:00 - 19:00)',
      'Acces în comunitatea VIP',
      'Masă / bufet separat pentru Ultra VIP',
      'Cină privată cu speakerii',
      'Networking cu speakerii',
      'Prioritate la întrebările adresate în Q&A',
      'Brain Notes – workbook fizic',
      'Minge Antistres',
      'Goodie Bag (în valoare de 200€)',
      'Aparatul de stimulare a nervului vag (în valoare de 1000€)'
    ]
  }
];

// Three cards: Standard (248-560€), VIP, Ultra VIP
const STANDARD_OPTIONS = ALL_PACKAGES.filter(p =>
  ['standard', 'standard-premium'].includes(p.id)
);
const VIP_OPTIONS = ALL_PACKAGES.filter(p => ['vip'].includes(p.id));
const ULTRA_OPTIONS = ALL_PACKAGES.filter(p => p.id === 'ultra-vip');

const PACKAGE_CARDS = [
  {
    id: 'standard',
    title: 'Standard',
    description: 'Pachete de la economic la premium',
    priceRange: '370-560',
    priceLabel: '395-449€',
    options: STANDARD_OPTIONS,
    benefits: ALL_PACKAGES.find(p => p.id === 'standard').benefits,
    brainMappingPeriod: 'programare după eveniment',
    featured: false,
    buttonText: 'Vreau pachetul Standard'
  },
  {
    id: 'vip',
    title: 'VIP',
    description: ALL_PACKAGES.find(p => p.id === 'vip').description,
    priceRange: null,
    priceLabel: '649€',
    options: VIP_OPTIONS,
    benefits: ALL_PACKAGES.find(p => p.id === 'vip').benefits,
    brainMappingPeriod: 'programare după eveniment',
    featured: true,
    buttonText: 'Vreau pachetul VIP'
  },
  {
    id: 'ultra-vip',
    title: 'Ultra VIP',
    description: ALL_PACKAGES.find(p => p.id === 'ultra-vip').description,
    priceRange: null,
    priceLabel: '2500€',
    options: ULTRA_OPTIONS,
    benefits: ALL_PACKAGES.find(p => p.id === 'ultra-vip').benefits,
    brainMappingPeriod: 'programare prioritară din momentul achiziției biletului',
    featured: false,
    buttonText: 'Vreau pachetul Ultra VIP'
  }
];

const EARLY_BIRD_OFFERS = [
  {
    id: 'early-bird-230',
    title: 'Discount',
    price: '230€',
    availability: 'Nu mai sunt locuri disponibile',
    soldOut: true
  },
  {
    id: 'early-bird-248-live',
    title: 'Discount',
    price: '248€',
    availability: 'Nu mai sunt locuri disponibile',
    soldOut: true
  },
  {
    id: 'early-bird-258',
    title: 'Discount',
    price: '258€',
    availability: 'Nu mai sunt locuri disponibile',
    soldOut: true
  },
  {
    id: 'early-bird-270',
    title: 'Discount',
    price: '270€',
    availability: 'Locuri disponibile',
    soldOut: false,
    showCountdown: true,
    countdownDays: 10,
    availabilityKey: 'discount268'
  }
];

const EARLY_BIRD_CHECKOUT_OPTION = {
  id: 'early-bird',
  title: 'Discount',
};

function HomePage() {
  const [showPlayButton, setShowPlayButton] = useState(true);
  const { data: availableSeatsData } = useAvailableSeats();
  // Card Early Bird / Discount: countdown până la 20 martie 2026, sfârșit de zi (Bucharest)
  const earlyBirdCountdownTargetIso = useMemo(() => '2026-03-20T23:59:59+02:00', []);
  // Card Discount: countdown până la 8 aprilie 2026, sfârșit de zi (Bucharest)
  const discount268CountdownTargetIso = useMemo(() => '2026-04-08T23:59:59+02:00', []);

  // Popup (sticky) countdown: dată fixă 4 aprilie 2026 – nu se resetează la refresh
  useEffect(() => {
    window.pricingTimerTargetDate = '2026-04-04T23:59:59+02:00';
    window.pricingTiers = PRICING_TIERS;
    if (window.initCountdown) {
      window.initCountdown();
    }
  }, []);
  const [modalPackageId, setModalPackageId] = useState(null);
  const [selectedModalOptionId, setSelectedModalOptionId] = useState(null);

  const openPackageModal = (cardId) => {
    const card = PACKAGE_CARDS.find(c => c.id === cardId);
    setModalPackageId(cardId);
    setSelectedModalOptionId(card?.options?.[0]?.id || null);
  };
  const closePackageModal = () => {
    setModalPackageId(null);
    setSelectedModalOptionId(null);
  };

  useEffect(() => {
    if (modalPackageId) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [modalPackageId]);

  const SEAT_IMAGE_BY_OPTION_ID = {
    'standard-economy': '/seats/early-bird-seats.jpg',
    'standard-discount': '/seats/standard-discount-seats.webp',
    'standard': '/seats/standard-seats.jpg',
    'standard-premium': '/seats/standard-premium-seats.jpg',
    'vip': '/seats/vip-seats.jpg',
    'ultra-vip': '/seats/ultra-vip-seats.webp'
  };
  const modalCard = modalPackageId ? PACKAGE_CARDS.find(c => c.id === modalPackageId) : null;
  const selectedModalOption = modalCard?.options?.find(o => o.id === selectedModalOptionId) || modalCard?.options?.[0] || null;

  const buildCheckoutUrl = (option) => {
    const destination = new URL('https://my.brain-event.com');
    const currentParams = new URLSearchParams(window.location.search);

    // Keep all marketing/tracking params from current landing URL
    currentParams.forEach((value, key) => {
      destination.searchParams.append(key, value);
    });

    if (option) {
      destination.searchParams.set('ticket_id', option.id);
      destination.searchParams.set('direct_seat_selector', '1');
    }

    return destination.toString();
  };

  const redirectToCheckout = (option) => {
    if (!option) return;
    window.location.href = buildCheckoutUrl(option);
  };

  // Calculate current pricing based on static PRICING_TIERS
  const currentPricing = useMemo(() => {
    const today = new Date();
    const currentTier = PRICING_TIERS.find(tier => {
      const start = new Date(tier.startDate);
      const end = new Date(tier.endDate);
      return today >= start && today <= end;
    }) || PRICING_TIERS[0]; // Default to first tier if no match

    // Calculate discount from period 7 (last period)
    const lastPeriod = PRICING_TIERS[6];
    const standardDiscount = Math.round(((lastPeriod.standard - currentTier.standard) / lastPeriod.standard) * 100);
    const vipDiscount = Math.round(((lastPeriod.vip - currentTier.vip) / lastPeriod.vip) * 100);
    const ultraVipDiscount = Math.round(((lastPeriod.ultraVip - currentTier.ultraVip) / lastPeriod.ultraVip) * 100);

    return {
      standard: {
        price: 395,
        discount: standardDiscount
      },
      vip: {
        price: 649,
        discount: vipDiscount
      },
      ultraVip: {
        price: 2500,
        discount: ultraVipDiscount
      }
    };
  }, []);

  const handlePlayClick = () => {
    setShowPlayButton(false);
  };
  return (
    <>
      <Navbar />
      <SpeakersWidget />
      

    {/* Sticky Navigation */}
    {/* Navbar component */}

    {/* Hero Section */}
    <section className="hero" id="hero" style={{paddingBottom: '3rem'}}>
        {/* Video Background */}
        <video 
            className="hero-background-video"
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            onLoadedMetadata={(e) => {
                e.target.play();
            }}
            onEnded={(e) => {
                e.target.currentTime = 0;
                e.target.play();
            }}
        >
            <source src="/assets/video-hero.webm" type="video/webm" />
        </video>
        
        <div className="container">
            <div className="hero-content">
                <div className="hero-badge" style={{marginBottom: '10px'}}>
                    <span className="pulse-dot"></span>
                    Eveniment Fizic • București 2026
                </div>
                
                <h1 className="hero-title" style={{marginBottom: '10px'}}>
                    Totul pentru <span className="gradient-text">sănătatea creierului</span>, într-un singur eveniment
                </h1>
                
                <p className="hero-subtitle">
                    Eveniment fizic cu <strong>5 speakeri internaționali</strong> și <strong>3 speakeri români</strong> de top
                </p>

                {/* VSL Video */}
                <div className="vsl-video-wrapper" style={{ position: 'relative' }}>
                    <iframe 
                        className="vsl-video"
                        src={`https://www.youtube.com/embed/lR2wsjOUHWM${showPlayButton ? '' : '?autoplay=1'}`}
                        title="Brain Event - Video Presentation"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        style={{
                            width: '100%',
                            height: '100%',
                            border: 'none',
                            display: showPlayButton ? 'none' : 'block'
                        }}
                    ></iframe>
                    
                    {showPlayButton && (
                        <>
                            <img 
                                src="/assets/thumbnail-video.webp" 
                                alt="Video Thumbnail"
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    display: 'block'
                                }}
                            />
                            <div className="vsl-play-overlay" onClick={handlePlayClick}>
                                <button className="vsl-play-button" aria-label="Redare video">
                                    <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
                                        <circle cx="40" cy="40" r="40" fill="rgba(6, 182, 212, 0.9)" />
                                        <path d="M32 25L55 40L32 55V25Z" fill="white" />
                                    </svg>
                                </button>
                            </div>
                        </>
                    )}
                </div>

                {/* CTA Principal */}
                <div className="hero-cta">
                    <ExternalLink href="https://my.brain-event.com" className="btn btn-large btn-primary btn-pulse">
                        <span>Rezervă locul acum</span>
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </ExternalLink>
                </div>
            </div>
        </div>
    </section>

    {/* Speakers Section - Gallery Style - Combined International & National */}
    <section className="speakers" id="speakeri">
        <div className="container">
            <div className="speakers-gallery-wrapper">
                {/* Main Gallery Grid - Custom Layout */}
                <div className="speakers-gallery-grid">
                    {/* Row 1 - Coloana 1: Matthew Walker (nivel normal) */}
                    <Link to="/matthew-walker" className="speaker-gallery-card" id="matthew-walker">
                        <div className="speaker-gallery-image">
                            <img src="/assets/matthew-walker-speaker.webp" alt="Matthew Walker" loading="lazy" />
                        </div>
                        <div className="speaker-gallery-content">
                            <h3 className="speaker-gallery-name">MATTHEW WALKER</h3>
                            <p className="speaker-gallery-role">Expert în Somnologie • Autor Bestseller</p>
                            <SpeakerSocialLinks speakerId="matthew-walker" stopPropagation />
                        </div>
                    </Link>

                    {/* Row 1 - Coloana 2: David Sinclair (mai sus) */}
                    <Link to="/david-sinclair" className="speaker-gallery-card speaker-gallery-elevated" id="david-sinclair">
                        <div className="speaker-gallery-image">
                            <img src="/david-sinclair-headshot-2024.png" alt="David Sinclair" loading="lazy" />
                        </div>
                        <div className="speaker-gallery-content">
                            <h3 className="speaker-gallery-name">DAVID SINCLAIR</h3>
                            <p className="speaker-gallery-role">Cercetător în genetică & expert global în longevity</p>
                            <SpeakerSocialLinks speakerId="david-sinclair" stopPropagation />
                        </div>
                    </Link>

                    {/* Row 1 - Coloana 3: Dr. Daniel Amen (nivel cu Matthew) */}
                    <Link to="/daniel-amen" className="speaker-gallery-card" id="dr-amen">
                        <div className="speaker-gallery-image">
                            <img src="/assets/amen-speaker.webp" alt="Dr. Daniel Amen" loading="lazy" />
                        </div>
                        <div className="speaker-gallery-content">
                            <h3 className="speaker-gallery-name">DR. DANIEL AMEN</h3>
                            <p className="speaker-gallery-role">Medic în Sănătatea Creierului • Prezență Online</p>
                            <SpeakerSocialLinks speakerId="daniel-amen" stopPropagation />
                        </div>
                    </Link>

                    {/* Row 2 */}
                    {/* Diana Palotas */}
                    <Link to="/diana-palotas" className="speaker-gallery-card" id="diana-palotas">
                        <div className="speaker-gallery-image">
                            <img src="/assets/Diana-Palotas-card.webp" alt="Diana Palotas" loading="lazy" />
                        </div>
                        <div className="speaker-gallery-content">
                            <h3 className="speaker-gallery-name">DIANA PALOTAS</h3>
                            <p className="speaker-gallery-role">Specialist în neuroștiință aplicată</p>
                            <SpeakerSocialLinks speakerId="diana-palotas" stopPropagation />
                        </div>
                    </Link>

                    {/* Niculina Gheorghiță */}
                    <Link to="/niculina-gheorghita" className="speaker-gallery-card" id="niculina">
                        <div className="speaker-gallery-image">
                            <img src="/assets/niculina-psiholog.webp" alt="Niculina Gheorghiță" loading="lazy" />
                        </div>
                        <div className="speaker-gallery-content">
                            <h3 className="speaker-gallery-name">NICULINA GHEORGHIȚĂ</h3>
                            <p className="speaker-gallery-role">Psiholog • Autor • Trainer</p>
                            <SpeakerSocialLinks speakerId="niculina-gheorghita" stopPropagation />
                        </div>
                    </Link>

                    {/* Speaker Misterios 3 */}
                    <div className="speaker-gallery-card speaker-gallery-mysterious">
                        <div className="speaker-gallery-image">
                            <img src="/mysterious-speaker-new.png" alt="Speaker misterios" loading="lazy" />
                            <div className="mysterious-badge-gallery">VA FI ANUNȚAT</div>
                        </div>
                        <div className="speaker-gallery-content">
                            <h3 className="speaker-gallery-name">SPECIALIST NEUROFEEDBACK</h3>
                            <p className="speaker-gallery-role">Experiență cu sportivi de elită</p>
                        </div>
                    </div>

                    {/* Row 3 */}
                    {/* Speaker Național - Medic */}
                    <div className="speaker-gallery-card speaker-gallery-mysterious">
                        <div className="speaker-gallery-image">
                            <img src="/mysterious-speaker-new.png" alt="Speaker național" loading="lazy" />
                            <div className="mysterious-badge-gallery">VA FI ANUNȚAT</div>
                        </div>
                        <div className="speaker-gallery-content">
                            <h3 className="speaker-gallery-name">MEDIC</h3>
                            <p className="speaker-gallery-role">Protocoale integrate</p>
                        </div>
                    </div>

                    {/* Speaker Național - Specialist */}
                    <div className="speaker-gallery-card speaker-gallery-mysterious">
                        <div className="speaker-gallery-image">
                            <img src="/mysterious-speaker-new.png" alt="Speaker național" loading="lazy" />
                            <div className="mysterious-badge-gallery">VA FI ANUNȚAT</div>
                        </div>
                        <div className="speaker-gallery-content">
                            <h3 className="speaker-gallery-name">SPECIALIST</h3>
                            <p className="speaker-gallery-role">Confirmat</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="section-cta">
                <a href="#pachete" className="btn btn-primary btn-large">Vreau să-i întâlnesc</a>
            </div>
        </div>
    </section>

    {/* David Sinclair Spotlight Section */}
    <SpeakerSpotlight
      name="David Sinclair"
      title="Expert mondial în longevitate • Harvard Medical School"
      badge="DAVID SINCLAIR — INVITAT SPECIAL"
      description="Cercetările lui au schimbat modul în care lumea înțelege îmbătrânirea, energia și sănătatea creierului. La Brain Event, explică simplu ce funcționează și cum aplici imediat."
      image="/david-sinclair-headshot-2024.png"
      readMoreLink="/david-sinclair"
    />

    {/* David Sinclair Book Showcase */}
    <BookShowcase
      badge="Bestseller mondial"
      mainTitle="Autorul cărții &quot;Lifespan&quot;"
      description="&quot;Lifespan&quot; este una dintre cele mai populare cărți despre longevitate și sănătate. La Brain Event, vei vedea principiile din carte explicate clar și aplicabil."
      bookImage="/assets/lifespan-book-david-sinclair.webp"
      primaryButtonText="Rezervă bilet"
      primaryButtonLink="https://my.brain-event.com"
      secondaryButtonText="Află mai multe"
      secondaryButtonLink="https://my.brain-event.com"
      backgroundColor="#F5F1E8"
    />

    {/* Pentru Cine Este Section */}
    <section className="for-who" id="pentru-cine-este">
        <div className="container">
            <div className="section-header">
                <h2 className="section-title">Pentru cine este?</h2>
                <p className="section-subtitle">Trei categorii de participanți care vor beneficia maxim</p>
            </div>

            <div className="persona-grid-new">
                {/* Persona 1: Interesați de sănătatea creierului */}
                <div className="persona-card-new">
                    <div className="persona-card-image" style={{backgroundImage: 'url(/assets/interesati.webp)'}}>
                        <div className="persona-card-title">
                            <h3>Interesați de sănătatea creierului</h3>
                        </div>
                    </div>
                    <div className="persona-card-overlay">
                        <div className="persona-card-content">
                            <div className="persona-card-title-overlay">
                                <h3>Interesați de sănătatea creierului</h3>
                            </div>
                            <div className="persona-quote">
                                "Vreau să am mai multă energie, focus și un somn mai bun."
                            </div>
                            <div className="persona-section">
                                <h4>Probleme frecvente:</h4>
                                <ul className="persona-problems">
                                    <li>Oboseală cronică</li>
                                    <li>Uitare</li>
                                    <li>Anxietate ușoară</li>
                                    <li>Somn dezordonat</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Persona 2: Biohackeri */}
                <div className="persona-card-new">
                    <div className="persona-card-image" style={{backgroundImage: 'url(/assets/biohackeri.webp)'}}>
                        <div className="persona-card-title">
                            <h3>Biohackeri & Optimizatori de performanță</h3>
                        </div>
                    </div>
                    <div className="persona-card-overlay">
                        <div className="persona-card-content">
                            <div className="persona-card-title-overlay">
                                <h3>Biohackeri & Optimizatori de performanță</h3>
                            </div>
                            <div className="persona-quote">
                                "Vreau protocoale simple, eficiente și rezultate măsurabile."
                            </div>
                            <div className="persona-section">
                                <h4>Probleme frecvente:</h4>
                                <ul className="persona-problems">
                                    <li>Rezultate în platou</li>
                                    <li>Prea multe informații</li>
                                    <li>Rutine care nu produc efecte reale</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Persona 3: Profesioniști */}
                <div className="persona-card-new">
                    <div className="persona-card-image" style={{backgroundImage: 'url(/assets/profesionisti.webp)'}}>
                        <div className="persona-card-title">
                            <h3>Profesioniști</h3>
                        </div>
                    </div>
                    <div className="persona-card-overlay">
                        <div className="persona-card-content">
                            <div className="persona-card-title-overlay">
                                <h3>Profesioniști</h3>
                            </div>
                            <div className="persona-quote">
                                "Vreau tehnici noi cu care să-mi ajut clienții și pacienții imediat."
                            </div>
                            <div className="persona-section">
                                <h4>Probleme frecvente:</h4>
                                <ul className="persona-problems">
                                    <li>Persoane rezistente la intervenții</li>
                                    <li>Progrese lente</li>
                                    <li>Lipsă protocoale clare</li>
                                    <li>Stres profesional ridicat</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="section-cta">
                <a href="#pachete" className="btn btn-primary btn-large">Vreau să particip</a>
            </div>
        </div>
    </section>

    {/* Why Brain Event Section */}
    <WhySection />

    {/* Ce Obții Section */}
    <section className="benefits" id="ce-obtii">
        <div className="container">
            <div className="section-header">
                <h2 className="section-title">Ce obții la eveniment</h2>
                <p className="section-subtitle">Experiență completă pe 3 piloni fundamentali</p>
            </div>

            <div className="benefits-stories-grid">
                {/* Pilon 1: Înveți */}
                <div className="benefit-story-card" style={{backgroundImage: 'url(/assets/bazele-sanatatii.webp)'}}>
                    <div className="story-badge">Înveți</div>
                    <div className="story-content">
                        <h3 className="story-title">Bazele sănătății creierului</h3>
                        <p className="story-description">Somn, lumină, mișcare, alimentație, respirație – explicate clar și aplicabil.</p>
                    </div>
                </div>

                <div className="benefit-story-card" style={{backgroundImage: 'url(/assets/Identifici-cauzele-reale.webp)'}}>
                    <div className="story-badge">Înveți</div>
                    <div className="story-content">
                        <h3 className="story-title">Înțelegerea mecanismelor</h3>
                        <p className="story-description">Înțelegi mecanismele din spatele anxietății și energiei instabile.</p>
                    </div>
                </div>

                <div className="benefit-story-card" style={{backgroundImage: 'url(/assets/structurezi-ziua.webp)'}}>
                    <div className="story-badge">Înveți</div>
                    <div className="story-content">
                        <h3 className="story-title">Structurarea zilei pentru performanță</h3>
                        <p className="story-description">Model zilnic bazat pe ritmul circadian pentru eficiență mentală maximă.</p>
                    </div>
                </div>

                {/* Pilon 2: Aplici */}
                <div className="benefit-story-card benefit-story-featured" style={{backgroundImage: 'url(/assets/protocol-de-somn.webp)'}}>
                    <div className="story-badge story-badge-featured">Aplici</div>
                    <div className="story-content">
                        <h3 className="story-title">Protocol de somn pas cu pas</h3>
                        <p className="story-description">Plan complet seară → noapte → dimineață, implementabil imediat.</p>
                    </div>
                </div>

                <div className="benefit-story-card" style={{backgroundImage: 'url(/assets/ritualuri-rapide.webp)'}}>
                    <div className="story-badge">Aplici</div>
                    <div className="story-content">
                        <h3 className="story-title">Ritualuri rapide pentru energie</h3>
                        <p className="story-description">Tehnici de 5–10 minute pentru claritate, focus și reducerea stresului.</p>
                    </div>
                </div>

                <div className="benefit-story-card" style={{backgroundImage: 'url(/assets/plan-personal.webp)'}}>
                    <div className="story-badge">Aplici</div>
                    <div className="story-content">
                        <h3 className="story-title">Plan personal 7/30/90 zile</h3>
                        <p className="story-description">Structură clară pe etape pentru rezultate predictibile.</p>
                    </div>
                </div>

                {/* Pilon 3: Primești */}
                <div className="benefit-story-card" style={{backgroundImage: 'url(/assets/brain-notes.webp)'}}>
                    <div className="story-badge">Primești</div>
                    <div className="story-content">
                        <h3 className="story-title">Brain Notes — workbook complet</h3>
                        <p className="story-description">Protocoale, checklists, exerciții, planuri.</p>
                    </div>
                </div>

                <div className="benefit-story-card" style={{backgroundImage: 'url(/assets/3-instrumente.webp)'}}>
                    <div className="story-badge">Primești</div>
                    <div className="story-content">
                        <h3 className="story-title">3 instrumente de auto-monitorizare</h3>
                        <p className="story-description">Tracking ușor pentru focus, stres și somn.</p>
                    </div>
                </div>

                <div className="benefit-story-card benefit-story-card-brain-mapping" style={{backgroundImage: 'url(/assets/brain-mapping-inclus-pentru-toti.webp)'}}>
                    <div className="story-badge">Primești</div>
                    <div className="story-content">
                        <h3 className="story-title">Brain Mapping inclus pentru toți</h3>
                        <p className="story-description">Toți participanții primesc acces la Brain Mapping inclus în bilet (Standard: în valoare de 500€; VIP / Ultra VIP: cu Diana, în valoare de 1000€).</p>
                        <ul className="story-brain-mapping-details">
                            <li><strong>Standard:</strong> programare după eveniment</li>
                            <li><strong>VIP:</strong> programare după eveniment</li>
                            <li><strong>Ultra VIP:</strong> programare prioritară din momentul achiziției biletului</li>
                        </ul>
                    </div>
                </div>

                <div className="benefit-story-card" style={{backgroundImage: 'url(/assets/q&a-session.webp)'}}>
                    <div className="story-badge">Primești</div>
                    <div className="story-content">
                        <h3 className="story-title">Q&A live cu speakerii</h3>
                        <p className="story-description">Întrebări directe și răspunsuri aplicate.</p>
                    </div>
                </div>
            </div>

            <div className="section-cta">
                <ExternalLink href="https://my.brain-event.com" className="btn btn-primary btn-large">Cumpara un ticket</ExternalLink>
            </div>
        </div>
    </section>

    {/* Secțiune dedicată Brain Mapping */}
    <section className="brain-mapping-section" id="brain-mapping">
        <div className="container">
            <div className="section-header">
                <h2 className="section-title">Brain Mapping inclus pentru toți</h2>
                <p className="section-subtitle">Toți participanții primesc acces la Brain Mapping inclus în bilet</p>
            </div>
            <div className="brain-mapping-section-content">
                <div className="brain-mapping-section-visual">
                    <div className="brain-mapping-section-image" style={{backgroundImage: 'url(/assets/brain-mapping-inclus-pentru-toti.webp)'}} role="img" aria-label="Brain Mapping" />
                </div>
                <div className="brain-mapping-section-text">
                    <p className="brain-mapping-section-intro">
                        Indiferent de pachetul ales, primești Brain Mapping: Standard în valoare de <strong>500€</strong>, iar la VIP și Ultra VIP — <strong>Brain Mapping cu Diana</strong> în valoare de <strong>1000€</strong>. Programarea se face după eveniment, conform perioadelor de mai jos.
                    </p>
                    <ul className="brain-mapping-section-tiers">
                        <li className="brain-mapping-tier">
                            <span className="brain-mapping-tier-name">Standard</span>
                            <span className="brain-mapping-tier-period">Programare după eveniment</span>
                        </li>
                        <li className="brain-mapping-tier">
                            <span className="brain-mapping-tier-name">VIP</span>
                            <span className="brain-mapping-tier-period">Programare după eveniment</span>
                        </li>
                        <li className="brain-mapping-tier">
                            <span className="brain-mapping-tier-name">Ultra VIP</span>
                            <span className="brain-mapping-tier-period">Programare prioritară din momentul achiziției biletului</span>
                        </li>
                    </ul>
                    <div className="brain-mapping-section-cta">
                        <ExternalLink href="https://my.brain-event.com" className="btn btn-primary">Cumpară bilet</ExternalLink>
                    </div>
                </div>
            </div>
        </div>
    </section>

    {/* Ce Obții pe Termen Lung Section - Discover Layout */}
    <section className="long-term-benefits" id="termen-lung">
        <div className="container">
            <div className="section-header">
                <h2 className="section-title">Ce obții pe termen lung</h2>
                <p className="section-subtitle">Transformări durabile pentru sănătatea ta mentală</p>
            </div>

            <div className="discover-layout">
                {/* Coloana 1 - Left Benefits */}
                <div className="discover-benefit discover-benefit-tl">
                    <h4>Claritate mentală</h4>
                    <p>Capacitatea de a gândi clar, de a lua decizii rapide și de a-ți menține concentrarea pe termen lung.</p>
                </div>

                <div className="discover-benefit discover-benefit-tl2">
                    <h4>Somn optimizat</h4>
                    <p>Protocoale științifice pentru un somn profund și recuperator, esențial pentru sănătatea creierului.</p>
                </div>

                <div className="discover-benefit discover-benefit-tl3">
                    <h4>Energie constantă</h4>
                    <p>Menținerea unui nivel stabil de energie pe tot parcursul zilei, fără fluctuații sau prăbușiri.</p>
                </div>

                {/* Coloana 2 - Center: Image + Bottom Benefit */}
                <div className="discover-center">
                    <div className="discover-image">
                        <img className="discover-image-desktop" src="/assets/image-ce-obtii-pe-termen-lunk.webp" alt="Ce obții pe termen lung" loading="lazy" />
                        <img className="discover-image-mobile" src="/assets/mobile-ce-obtii-pe-termen-lung.webp" alt="Ce obții pe termen lung" loading="lazy" />
                    </div>
                </div>

                <div className="discover-benefit discover-benefit-bc">
                    <h4>Control real asupra stării mentale</h4>
                    <p>Abilitatea de a-ți gestiona emoțiile și starea mentală în orice situație.</p>
                </div>

                {/* Coloana 3 - Right Benefits */}
                <div className="discover-benefit discover-benefit-tr">
                    <h4>Reducerea stresului</h4>
                    <p>Tehnici dovedite pentru gestionarea eficientă a stresului și anxietății zilnice.</p>
                </div>

                <div className="discover-benefit discover-benefit-tr2">
                    <h4>Structură zilnică sănătoasă</h4>
                    <p>Crearea și menținerea unor obiceiuri care susțin sănătatea cognitivă pe termen lung.</p>
                </div>

                <div className="discover-benefit discover-benefit-tr3">
                    <h4>Memorie îmbunătățită</h4>
                    <p>Îmbunătățirea capacității de memorare și retenție a informațiilor importante.</p>
                </div>

            </div>

            <div className="section-cta">
                <a href="#pachete" className="btn btn-primary btn-large">Vreau transformarea</a>
            </div>
        </div>
    </section>

    {/* Testimonials Section */}
    <section className="testimonials" id="testimoniale">
        <div className="container">
            <div className="section-header">
                <h2 className="section-title">Ce spun participanții</h2>
                <p className="section-subtitle">Feedback autentic de la ediția anterioară</p>
            </div>

            {/* Stats First - Impact Numbers */}
            <div className="testimonial-stats-hero">
                <div className="stat-hero-item">
                    <div className="stat-hero-number">1000+</div>
                    <div className="stat-hero-label">Participanți</div>
                    <div className="stat-hero-sublabel">ediția trecută</div>
                </div>
                <div className="stat-hero-divider"></div>
                <div className="stat-hero-item stat-hero-featured">
                    <div className="stat-hero-number">10/10</div>
                    <div className="stat-hero-label">Rating mediu</div>
                    <div className="stat-hero-sublabel">satisfacție maximă</div>
                </div>
                <div className="stat-hero-divider"></div>
                <div className="stat-hero-item">
                    <div className="stat-hero-number">95%</div>
                    <div className="stat-hero-label">Recomandare</div>
                    <div className="stat-hero-sublabel">ar participa din nou</div>
                </div>
            </div>

            {/* Testimonials Grid */}
            <div className="testimonials-grid-modern">
                {/* Testimonial 1 - Featured Large */}
                <div className="testimonial-card-modern featured-large">
                    <div className="testimonial-quote-icon">"</div>
                    <div className="testimonial-rating">
                        <span className="star-filled">★</span>
                        <span className="star-filled">★</span>
                        <span className="star-filled">★</span>
                        <span className="star-filled">★</span>
                        <span className="star-filled">★</span>
                    </div>
                    <p className="testimonial-text-modern">"Am plecat cu un plan concret pentru somn și am văzut rezultate în doar 5 zile. Claritate mentală pe care nu am mai simțit-o de ani. Protocoalele sunt simple dar puternice."</p>
                    <div className="testimonial-author-modern">
                        <div className="author-avatar-modern">
                            <span>AM</span>
                        </div>
                        <div className="author-details">
                            <strong className="author-name-modern">Ana Maria</strong>
                            <span className="author-role-modern">Antreprenor, 34 ani</span>
                            <span className="author-result">✓ Focus +40% în 7 zile</span>
                        </div>
                    </div>
                </div>

                {/* Testimonial 2 */}
                <div className="testimonial-card-modern">
                    <div className="testimonial-rating">
                        <span className="star-filled">★</span>
                        <span className="star-filled">★</span>
                        <span className="star-filled">★</span>
                        <span className="star-filled">★</span>
                        <span className="star-filled">★</span>
                    </div>
                    <p className="testimonial-text-modern">"Ca psiholog, am găsit protocoale practice pe care le-am integrat imediat cu clienții mei. Schimbarea a fost vizibilă în primele 2 săptămâni."</p>
                    <div className="testimonial-author-modern">
                        <div className="author-avatar-modern">
                            <span>RP</span>
                        </div>
                        <div className="author-details">
                            <strong className="author-name-modern">Radu Popa</strong>
                            <span className="author-role-modern">Psiholog clinician</span>
                        </div>
                    </div>
                </div>

                {/* Testimonial 3 */}
                <div className="testimonial-card-modern">
                    <div className="testimonial-rating">
                        <span className="star-filled">★</span>
                        <span className="star-filled">★</span>
                        <span className="star-filled">★</span>
                        <span className="star-filled">★</span>
                        <span className="star-filled">★</span>
                    </div>
                    <p className="testimonial-text-modern">"Informații dense dar accesibile. Am ieșit cu instrumente măsurabile și o comunitate de oameni pasionați de aceleași lucruri."</p>
                    <div className="testimonial-author-modern">
                        <div className="author-avatar-modern">
                            <span>MC</span>
                        </div>
                        <div className="author-details">
                            <strong className="author-name-modern">Maria Cristea</strong>
                            <span className="author-role-modern">Biohacker & Coach</span>
                        </div>
                    </div>
                </div>

                {/* Testimonial 4 */}
                <div className="testimonial-card-modern">
                    <div className="testimonial-rating">
                        <span className="star-filled">★</span>
                        <span className="star-filled">★</span>
                        <span className="star-filled">★</span>
                        <span className="star-filled">★</span>
                        <span className="star-filled">★</span>
                    </div>
                    <p className="testimonial-text-modern">"Mi-am rezolvat problemele de somn după 10 ani de încercări. Acum dorm 7h/noapte și mă trezesc energizat. Schimbător de viață!"</p>
                    <div className="testimonial-author-modern">
                        <div className="author-avatar-modern">
                            <span>DI</span>
                        </div>
                        <div className="author-details">
                            <strong className="author-name-modern">Dan Ionescu</strong>
                            <span className="author-role-modern">Developer, 29 ani</span>
                        </div>
                    </div>
                </div>

                {/* Testimonial 5 - Video Style */}
                <div className="testimonial-card-modern video-style">
                    <div className="video-badge">🎥 Recenzie Video</div>
                    <div className="testimonial-rating">
                        <span className="star-filled">★</span>
                        <span className="star-filled">★</span>
                        <span className="star-filled">★</span>
                        <span className="star-filled">★</span>
                        <span className="star-filled">★</span>
                    </div>
                    <p className="testimonial-text-modern">"Cel mai practic eveniment la care am participat. Zero fluff, doar acțiuni concrete. Workbook-ul e aur curat!"</p>
                    <div className="testimonial-author-modern">
                        <div className="author-avatar-modern">
                            <span>SI</span>
                        </div>
                        <div className="author-details">
                            <strong className="author-name-modern">Simona Ilie</strong>
                            <span className="author-role-modern">Manager HR</span>
                        </div>
                    </div>
                </div>

                {/* Testimonial 6 */}
                <div className="testimonial-card-modern">
                    <div className="testimonial-rating">
                        <span className="star-filled">★</span>
                        <span className="star-filled">★</span>
                        <span className="star-filled">★</span>
                        <span className="star-filled">★</span>
                        <span className="star-filled">★</span>
                    </div>
                    <p className="testimonial-text-modern">"Am venit sceptic, am plecat convins. Informațiile sunt bazate pe știință, nu pe hype. Super valoros pentru echipa mea."</p>
                    <div className="testimonial-author-modern">
                        <div className="author-avatar-modern">
                            <span>AL</span>
                        </div>
                        <div className="author-details">
                            <strong className="author-name-modern">Alex Luca</strong>
                            <span className="author-role-modern">CEO Startup</span>
                        </div>
                    </div>
                </div>

                {/* Testimonial 7 */}
                <div className="testimonial-card-modern">
                    <div className="testimonial-rating">
                        <span className="star-filled">★</span>
                        <span className="star-filled">★</span>
                        <span className="star-filled">★</span>
                        <span className="star-filled">★</span>
                        <span className="star-filled">★</span>
                    </div>
                    <p className="testimonial-text-modern">"M-a impresionat nivelul de profesionalism și calitatea informațiilor. Am aplicat tehnicile de respirație și deja simt diferența în gestionarea stresului."</p>
                    <div className="testimonial-author-modern">
                        <div className="author-avatar-modern">
                            <span>IC</span>
                        </div>
                        <div className="author-details">
                            <strong className="author-name-modern">Ioana Constantin</strong>
                            <span className="author-role-modern">Avocat, 41 ani</span>
                        </div>
                    </div>
                </div>

                {/* Testimonial 8 */}
                <div className="testimonial-card-modern">
                    <div className="testimonial-rating">
                        <span className="star-filled">★</span>
                        <span className="star-filled">★</span>
                        <span className="star-filled">★</span>
                        <span className="star-filled">★</span>
                        <span className="star-filled">★</span>
                    </div>
                    <p className="testimonial-text-modern">"Exact ce aveam nevoie pentru a înțelege legătura dintre alimentație și performanță mentală. Recomandările sunt clare și ușor de implementat în viața de zi cu zi."</p>
                    <div className="testimonial-author-modern">
                        <div className="author-avatar-modern">
                            <span>MV</span>
                        </div>
                        <div className="author-details">
                            <strong className="author-name-modern">Mihai Vasile</strong>
                            <span className="author-role-modern">Arhitect, 36 ani</span>
                        </div>
                    </div>
                </div>

                {/* Testimonial 9 */}
                <div className="testimonial-card-modern">
                    <div className="testimonial-rating">
                        <span className="star-filled">★</span>
                        <span className="star-filled">★</span>
                        <span className="star-filled">★</span>
                        <span className="star-filled">★</span>
                        <span className="star-filled">★</span>
                    </div>
                    <p className="testimonial-text-modern">"Eveniment excelent! Am învățat tehnici concrete pentru a-mi gestiona mai bine energia pe parcursul zilei. În doar 2 săptămâni am observat o îmbunătățire semnificativă."</p>
                    <div className="testimonial-author-modern">
                        <div className="author-avatar-modern">
                            <span>CT</span>
                        </div>
                        <div className="author-details">
                            <strong className="author-name-modern">Carmen Toma</strong>
                            <span className="author-role-modern">Designer, 32 ani</span>
                        </div>
                    </div>
                </div>

                {/* Testimonial 10 */}
                <div className="testimonial-card-modern">
                    <div className="testimonial-rating">
                        <span className="star-filled">★</span>
                        <span className="star-filled">★</span>
                        <span className="star-filled">★</span>
                        <span className="star-filled">★</span>
                        <span className="star-filled">★</span>
                    </div>
                    <p className="testimonial-text-modern">"Investiție care merită fiecare ban. Protocoalele de optimizare cognitivă sunt ușor de urmat și rezultatele sunt vizibile rapid. Concentrarea mi s-a dublat!"</p>
                    <div className="testimonial-author-modern">
                        <div className="author-avatar-modern">
                            <span>VG</span>
                        </div>
                        <div className="author-details">
                            <strong className="author-name-modern">Victor Gheorghe</strong>
                            <span className="author-role-modern">Consultant, 38 ani</span>
                        </div>
                    </div>
                </div>

                {/* Testimonial 11 */}
                <div className="testimonial-card-modern">
                    <div className="testimonial-rating">
                        <span className="star-filled">★</span>
                        <span className="star-filled">★</span>
                        <span className="star-filled">★</span>
                        <span className="star-filled">★</span>
                        <span className="star-filled">★</span>
                    </div>
                    <p className="testimonial-text-modern">"Ca medic, sunt foarte exigent cu informațiile pe care le primesc. Aici totul este bazat pe cercetări serioase și aplicabil în practică. Recomand cu încredere!"</p>
                    <div className="testimonial-author-modern">
                        <div className="author-avatar-modern">
                            <span>LP</span>
                        </div>
                        <div className="author-details">
                            <strong className="author-name-modern">Dr. Laura Petre</strong>
                            <span className="author-role-modern">Medic de familie</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="section-cta">
                <a href="#pachete" className="btn btn-primary btn-large">Vreau și eu rezultate</a>
            </div>
        </div>
    </section>

    {/* Endorsements Section - Hidden */}
    {/* <EndorsementsSection
      title="Endorsed by the world's most influential voices"
      subtitle="De la antreprenori de succes la profesioniști din domeniul sănătății, metodele prezentate la Brain Event sunt încredințate de cei care caută performanță maximă și longevitate."
      endorsements={[
        {
          quote: "Sănătatea creierului este fundamentul pentru tot ce faci în viață. Dacă vrei să fii performant, să ai energie și să îmbătrânești sănătos, trebuie să înveți să-ți optimizezi funcțiile cognitive.",
          name: "Dr. Alexandru Popescu",
          role: "Medic neurolog • 20+ ani experiență",
          image: "/anca.jpeg"
        },
        {
          quote: "Am aplicat protocoalele de optimizare a somnului și a fost o transformare completă. Energia mea s-a dublat, claritatea mentală e la nivel maxim. Aceste metode chiar funcționează.",
          name: "Maria Ionescu",
          role: "Antreprenor • CEO Tech Startup",
          image: "/assets/Diana-Palotas-card.webp"
        },
        {
          quote: "Ca psiholog clinician, am integrat tehnicile de neuroplasticitate cu clienții mei și rezultatele sunt extraordinare. Brain mapping-ul oferă insights pe care terapia tradițională nu le poate oferi.",
          name: "Prof. Dan Mihăilescu",
          role: "Psiholog clinician • Specialist în neuroștiințe",
          image: "/santiago.jpg"
        }
      ]}
    /> */}

    {/* CTA Section */}
    <CTASection
      title="Pregătit pentru Brain Event?"
      subtitle="Locurile sunt limitate. Prețurile cresc treptat."
      buttonText="Înscrie-te pentru 2026"
      buttonLink="#pachete"
      date="20.06.2026"
      venue="Sala Palatului"
      location="București"
    />

    {/* Packages Section */}
    <section className="packages" id="pachete">
        <div className="container">
            <div className="section-header">
                <h2 className="section-title">Pachete și locuri</h2>
                <p className="section-subtitle">Prețurile afișate sunt valabile până la data de 01.03.2026</p>
                <p className="section-subtitle">Alege varianta potrivită pentru tine</p>
            </div>

            <div className="packages-mobile-order">
            <div className="early-bird-list" aria-label="Oferte Discount">
                {EARLY_BIRD_OFFERS.map((offer) => {
                    const availabilityBucket = offer.availabilityKey
                      ? (availableSeatsData?.[offer.availabilityKey] ??
                          availableSeatsData?.[offer.availabilityKey.replace(/[A-Z]/g, (m) => `_${m.toLowerCase()}`)] ??
                          availableSeatsData?.discount_268)
                      : null;
                    const rawAvail =
                      availabilityBucket != null && typeof availabilityBucket === 'object'
                        ? Number(availabilityBucket.available)
                        : NaN;
                    const availableCount = Number.isFinite(rawAvail) ? rawAvail : null;
                    const soldOut = availableCount !== null ? availableCount === 0 : offer.soldOut;
                    const availability =
                      availableCount !== null
                        ? availableCount === 0
                          ? 'Nu mai sunt locuri disponibile'
                          : `${availableCount} locuri disponibile`
                        : offer.availability;
                    const countdownTarget = offer.id === 'early-bird-270' ? discount268CountdownTargetIso : earlyBirdCountdownTargetIso;
                    const checkoutOption = offer.id === 'early-bird-270'
                      ? { id: 'early-bird-270', title: 'Discount' }
                      : EARLY_BIRD_CHECKOUT_OPTION;
                    return (
                    <div
                        key={offer.id}
                        className={`early-bird-card ${soldOut ? 'is-sold-out' : ''} ${offer.showCountdown ? 'early-bird-card-three-cols' : ''}`}
                    >
                        <div className="early-bird-col early-bird-col-label">
                            <h3 className="early-bird-title">
                                {offer.title} <span className="early-bird-price">{offer.price}</span>
                            </h3>
                            <p className={`early-bird-status ${!soldOut ? 'early-bird-status-available' : ''}`}>{availability}</p>
                        </div>
                        {offer.showCountdown && (
                            <div className="early-bird-col early-bird-col-timer">
                                <div className="early-bird-countdown">
                                    <CountdownTimer targetDate={countdownTarget} wrapperId={`earlyBirdUrgencyTimer-${offer.id}`} />
                                </div>
                            </div>
                        )}
                        <div className="early-bird-col early-bird-col-cta">
                            {soldOut ? (
                                <button
                                    type="button"
                                    className="early-bird-btn is-disabled"
                                    disabled
                                >
                                    Epuizat
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className="early-bird-btn"
                                    onClick={() => redirectToCheckout(checkoutOption)}
                                >
                                    Vreau Bilet Discount
                                </button>
                            )}
                        </div>
                    </div>
                    );
                })}
            </div>

            {/* Three cards: Standard, VIP, Ultra VIP */}
            <div className="packages-three-cards">
                {PACKAGE_CARDS.map((card) => {
                    const benefits = card.benefits || [];
                    const displayPrice = card.priceLabel;
                    const buttonText = card.buttonText;
                    return (
                        <div
                            key={card.id}
                            className={`package-pricing-card package-card-${card.id} ${card.featured ? 'package-pricing-featured' : ''}`}
                        >
                            {card.featured && <div className="package-pricing-badge">Cel mai popular</div>}
                            
                            <div className="package-pricing-header">
                                <h3 className="package-pricing-title">{card.title}</h3>
                                <p className="package-pricing-description">{card.description}</p>
                            </div>

                            <div className={`package-price-display ${card.featured ? 'package-price-display-vip' : ''}`}>
                                <span className={`package-price ${card.featured ? 'package-price-vip' : ''}`}>{displayPrice}</span>
                            </div>

                            <div className="package-pricing-content">
                                <p className="package-pricing-label">Ce este inclus:</p>
                                <ul className="package-pricing-benefits">
                                    {benefits.map((benefit, idx) => (
                                        <li key={idx} className={benefit.includes('Brain Mapping') && card.brainMappingPeriod ? 'package-benefit-with-period' : ''}>
                                            <div className="package-checkmark">
                                                <GradientIcon type="checkmark" />
                                            </div>
                                            <span>
                                                {benefit}
                                                {benefit.includes('Brain Mapping') && card.brainMappingPeriod && (
                                                    <span className="benefit-period">{card.brainMappingPeriod}</span>
                                                )}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="package-pricing-footer">
                                {(card.id === 'standard') ? (
                                    <button
                                        type="button"
                                        className={`btn ${card.featured ? 'btn-vip-white' : 'btn-outline'} btn-block`}
                                        onClick={() => openPackageModal(card.id)}
                                    >
                                        {buttonText}
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        className={`btn ${card.featured ? 'btn-vip-white' : 'btn-outline'} btn-block`}
                                        onClick={() => redirectToCheckout(card.options?.[0])}
                                    >
                                        {buttonText}
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
            </div>

            {/* Modal bilete Standard */}
            {modalCard && ReactDOM.createPortal(
                <div className="package-tickets-modal-overlay" onClick={closePackageModal} role="dialog" aria-modal="true" aria-label="Bilete">
                    <div className="package-tickets-modal" onClick={(e) => e.stopPropagation()}>
                        <button type="button" className="package-tickets-modal-close" onClick={closePackageModal} aria-label="Închide">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M18 6L6 18M6 6l12 12"/>
                            </svg>
                        </button>
                        <h3 className="package-tickets-modal-title">Bilete {modalCard.title}</h3>
                        <div className="package-tickets-modal-grid">
                            {modalCard.options.map((option) => (
                                <div
                                    key={option.id}
                                    className={`package-tickets-option-card ${selectedModalOptionId === option.id ? 'is-selected' : ''}`}
                                    onClick={() => {
                                      setSelectedModalOptionId(option.id);
                                    }}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setSelectedModalOptionId(option.id);
                                      }
                                    }}
                                >
                                    {selectedModalOptionId === option.id && (
                                        <div className="package-tickets-option-check" aria-hidden="true">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                                <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </div>
                                    )}
                                    <div className="package-tickets-option-image">
                                        <img
                                            src={SEAT_IMAGE_BY_OPTION_ID[option.id] || '/seats/standard-seats.jpg'}
                                            alt={`Locuri ${option.title}`}
                                        />
                                    </div>
                                    <div className="package-tickets-option-header">
                                        <h4 className="package-tickets-option-title">{option.title}</h4>
                                        <span className="package-tickets-option-price">{option.price}€</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="package-tickets-modal-actions">
                            <button
                                type="button"
                                className="btn btn-primary btn-block package-tickets-modal-cta"
                                onClick={() => redirectToCheckout(selectedModalOption)}
                            >
                                Continuă spre înscriere
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* OLD GRID - TO BE REMOVED */}
            <div className="packages-pricing-grid" style={{display: 'none'}}>
                {/* Standard Package - Left */}
                <div className="package-pricing-card package-pricing-small">
                    {currentPricing.standard.discount > 0 && (
                      <div className="package-discount-badge">
                        Economisești {currentPricing.standard.discount}%
                      </div>
                    )}
                    <div className="package-pricing-header">
                        <h3 className="package-pricing-title">Standard</h3>
                        <p className="package-pricing-description">Pachet de bază pentru participarea la eveniment</p>
                    </div>
                    <div className="package-price-display">
                        <span className="package-price">{currentPricing.standard.price}€</span>
                    </div>
                    <div className="package-pricing-content">
                        <p className="package-pricing-label">Ce este inclus:</p>
                        <ul className="package-pricing-benefits">
                            <li>
                                <div className="package-checkmark">
                                    <GradientIcon type="checkmark" />
                                </div>
                                <span>Acces complet la eveniment</span>
                            </li>
                            <li>
                                <div className="package-checkmark">
                                    <GradientIcon type="checkmark" />
                                </div>
                                <span>Acces la toate prezentările speakerilor</span>
                            </li>
                            <li>
                                <div className="package-checkmark">
                                    <GradientIcon type="checkmark" />
                                </div>
                                <span>Cupon pentru prânz</span>
                            </li>
                            <li>
                                <div className="package-checkmark">
                                    <GradientIcon type="checkmark" />
                                </div>
                                <span>Brain Mapping (în valoare de 500€)</span>
                            </li>
                        </ul>
                    </div>
                    <div className="package-pricing-footer">
                        <ExternalLink href="https://my.brain-event.com" className="btn btn-outline btn-block">Solicită ofertă</ExternalLink>
                    </div>
                </div>

                {/* VIP Package - Center (Featured) */}
                <div className="package-pricing-card package-pricing-featured" style={{backgroundImage: 'url(/assets/background-cards.webp)'}}>
                    <div className="package-pricing-badge">Cel mai popular</div>
                    {currentPricing.vip.discount > 0 && (
                      <div className="package-discount-badge package-discount-badge-vip">
                        Economisești {currentPricing.vip.discount}%
                      </div>
                    )}
                    <div className="package-pricing-header">
                        <h3 className="package-pricing-title">VIP</h3>
                        <p className="package-pricing-description">Experiență premium cu acces exclusiv</p>
                    </div>
                    <div className="package-price-display package-price-display-vip">
                        <span className="package-price package-price-vip">{currentPricing.vip.price}€</span>
                    </div>
                    <div className="package-pricing-content">
                        <p className="package-pricing-label">Ce este inclus:</p>
                        <ul className="package-pricing-benefits">
                            <li>
                                <div className="package-checkmark">
                                    <GradientIcon type="checkmark" />
                                </div>
                                <span>Include TOT din Standard, plus:</span>
                            </li>
                            <li>
                                <div className="package-checkmark">
                                    <GradientIcon type="checkmark" />
                                </div>
                                <span>Locuri în zona premium (în față)</span>
                            </li>
                            <li>
                                <div className="package-checkmark">
                                    <GradientIcon type="checkmark" />
                                </div>
                                <span>Acces în comunitatea VIP</span>
                            </li>
                            <li>
                                <div className="package-checkmark">
                                    <GradientIcon type="checkmark" />
                                </div>
                                <span>Brain Notes – workbook fizic</span>
                            </li>
                            <li>
                                <div className="package-checkmark">
                                    <GradientIcon type="checkmark" />
                                </div>
                                <span>Goodie Bag (în valoare de 100€)</span>
                            </li>
                            <li>
                                <div className="package-checkmark">
                                    <GradientIcon type="checkmark" />
                                </div>
                                <span>Brain Mapping cu Diana (în valoare de 1000€)</span>
                            </li>
                        </ul>
                    </div>
                    <div className="package-pricing-footer">
                        <ExternalLink href="https://my.brain-event.com" className="btn btn-vip-white btn-block">Vreau pachetul VIP</ExternalLink>
                    </div>
                </div>

                {/* Ultra VIP Package - Right */}
                <div className="package-pricing-card package-pricing-small">
                    {currentPricing.ultraVip.discount > 0 && (
                      <div className="package-discount-badge">
                        Economisești {currentPricing.ultraVip.discount}%
                      </div>
                    )}
                    <div className="package-pricing-header">
                        <h3 className="package-pricing-title">Ultra VIP</h3>
                        <p className="package-pricing-description">Acces exclusiv cu experiență completă</p>
                    </div>
                    <div className="package-price-display">
                        <span className="package-price">{currentPricing.ultraVip.price}€</span>
                    </div>
                    <div className="package-pricing-content">
                        <p className="package-pricing-label">Ce este inclus:</p>
                        <ul className="package-pricing-benefits">
                            <li>
                                <div className="package-checkmark">
                                    <GradientIcon type="checkmark" />
                                </div>
                                <span>Include TOT din VIP, plus:</span>
                            </li>
                            <li>
                                <div className="package-checkmark">
                                    <GradientIcon type="checkmark" />
                                </div>
                                <span>Locuri în primele 4 rânduri</span>
                            </li>
                            <li>
                                <div className="package-checkmark">
                                    <GradientIcon type="checkmark" />
                                </div>
                                <span>Masă/Bufet separat pentru Ultra VIP</span>
                            </li>
                            <li>
                                <div className="package-checkmark">
                                    <GradientIcon type="checkmark" />
                                </div>
                                <span>Acces prioritar în sala</span>
                            </li>
                            <li>
                                <div className="package-checkmark">
                                    <GradientIcon type="checkmark" />
                                </div>
                                <span>Cină privată cu speakerii internaționali</span>
                            </li>
                            <li>
                                <div className="package-checkmark">
                                    <GradientIcon type="checkmark" />
                                </div>
                                <span>Networking cu speakerii</span>
                            </li>
                            <li>
                                <div className="package-checkmark">
                                    <GradientIcon type="checkmark" />
                                </div>
                                <span>Prioritate la întrebările adresate în Q&A</span>
                            </li>
                            <li>
                                <div className="package-checkmark">
                                    <GradientIcon type="checkmark" />
                                </div>
                                <span>Aparatul de stimulare la nervul vag (în valoare de 1000€)</span>
                            </li>
                            <li>
                                <div className="package-checkmark">
                                    <GradientIcon type="checkmark" />
                                </div>
                                <span>Goodie Bag (în valoare de 200€)</span>
                            </li>
                        </ul>
                    </div>
                    <div className="package-pricing-footer">
                        <ExternalLink href="https://my.brain-event.com" className="btn btn-outline btn-block">Solicită ofertă Ultra VIP</ExternalLink>
                    </div>
                </div>
            </div>
            {/* END OLD GRID */}
        </div>
    </section>

    {/* Group Packages Section - entire section commented out (Pachete de grup) */}
    {/* <GroupPackages /> */}

    {/* Agenda Section - Hidden */}
    {/* <AgendaSection /> */}

    {/* FAQ Section */}
    <section className="faq" id="intrebari-frecvente">
        <div className="container">
            <div className="faq-header-new">
                <p className="faq-eyebrow">Întrebări Frecvente</p>
                <h2 className="faq-main-title">Tot ce trebuie să știi: Răspunsuri clare la întrebările tale</h2>
                <div className="faq-cta-buttons">
                    <a href="#pachete" className="btn btn-primary btn-large">Înscrie-te acum</a>
                    <a href="#pachete" className="btn btn-outline btn-large">Vezi pachete</a>
                </div>
            </div>

            <div className="faq-container">
                <div className="faq-item active">
                    <button className="faq-question">
                        <span>Unde și când are loc evenimentul?</span>
                        <svg className="faq-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                    <div className="faq-answer">
                        <p>20 iunie 2026 • București • Sala Palatului</p>
                    </div>
                </div>

                <div className="faq-item">
                    <button className="faq-question">
                        <span>Cine poate participa?</span>
                        <svg className="faq-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                    <div className="faq-answer">
                        <p>Oricine interesat de sănătatea creierului.<br />Nu este nevoie de experiență în domeniu.</p>
                    </div>
                </div>

                <div className="faq-item">
                    <button className="faq-question">
                        <span>Brain Mapping-ul este inclus?</span>
                        <svg className="faq-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                    <div className="faq-answer">
                        <p>Vei primi un voucher gratuit, iar validarea acestuia se va face prin prezența la eveniment. Programarea la brain mapping se face doar după eveniment.</p>
                    </div>
                </div>

                <div className="faq-item">
                    <button className="faq-question">
                        <span>Există înregistrare?</span>
                        <svg className="faq-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                    <div className="faq-answer">
                        <p>Nu. Eveniment exclusiv LIVE în locație.</p>
                    </div>
                </div>

                <div className="faq-item">
                    <button className="faq-question">
                        <span>Este masa inclusă?</span>
                        <svg className="faq-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                    <div className="faq-answer">
                        <p>Pentru a păstra opțiunile Early Bird și Standard cât mai accesibile, acestea nu includ masa.<br />
                        Pachetele VIP și Ultra VIP includ cupon pentru prânz + coffee breaks.<br />
                        În plus, Ultra VIP include și cina cu speakerii.</p>
                    </div>
                </div>
            </div>

            <div className="section-cta">
                <p className="cta-text">Nu ai găsit răspunsul?</p>
                <a href="#pachete" className="btn btn-secondary btn-large">Contactează-ne direct</a>
            </div>
        </div>
    </section>

    {/* Sticky Bottom Banner */}
    <div className="sticky-bottom-banner" id="stickyBanner">
        <button 
            className="banner-close" 
            onClick={(e) => {
                const banner = e.target.closest('.sticky-bottom-banner');
                if (banner) banner.style.display = 'none';
            }}
            aria-label="Închide banner"
        >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
        </button>
        <div className="banner-container">
            <div className="banner-content">
                <div className="banner-text">
                    <h3>Până la următoarea scumpire</h3>
                    <p>Locuri limitate • Early Access</p>
                </div>
                <div className="banner-timer" id="urgencyTimer">
                    <div className="timer-block">
                        <span className="timer-number" id="days">00</span>
                        <span className="timer-label">Zile</span>
                    </div>
                    <div className="timer-divider">:</div>
                    <div className="timer-block">
                        <span className="timer-number" id="hours">00</span>
                        <span className="timer-label">Ore</span>
                    </div>
                    <div className="timer-divider">:</div>
                    <div className="timer-block">
                        <span className="timer-number" id="minutes">00</span>
                        <span className="timer-label">Min</span>
                    </div>
                    <div className="timer-divider">:</div>
                    <div className="timer-block">
                        <span className="timer-number" id="seconds">00</span>
                        <span className="timer-label">Sec</span>
                    </div>
                </div>
                <ExternalLink href="https://my.brain-event.com" className="btn btn-large btn-white-banner">Rezervă acum locul</ExternalLink>
            </div>
        </div>
    </div>

    {/* Become a Sponsor Section */}
    <CTASection
      title="Devino Sponsor la Brain Event 2026"
      subtitle="Alătură-te comunității Brain Event ca sponsor și conectează-te cu peste 4.000 de profesioniști."
      buttonText="Aplică pentru Sponsorizare"
      buttonLink={SPONSOR_FORM_URL}
      date="4.000+"
      venue="Participanți"
      location="Brain Event 2026"
    />

    {/* Footer */}
    {/* Footer component */}

    
    
    {/* Disabled - Too many WebGL contexts */}
    

      <Footer />
    </>
  );
}

export default HomePage;
